import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

interface InstitutionResponse {
  data: any
  resources: any
}

const tourStopFilter = (stopId, type) => {
  let filter
  let properties = []
  if (stopId !== 'all' && parseInt(stopId) !== -1) {
    properties.push({
      column: 'stopid',
      op: 'eq',
      value: stopId,
    })
  }
  if (type === 'photos') {
    properties.push(
      {
        column: 'official',
        op: 'eq',
        value: 1,
      },
      { column: 'status', op: 'eq', value: '1' }
    )
  }
  if (type === 'panoramas' || type === 'photos') {
    properties.push({
      column: 'status',
      op: 'nin',
      value: [-1, 5],
    })
  }
  if (properties.length > 0) {
    filter = {
      operation: 'AND',
      properties: properties,
    }
  }
  return JSON.stringify(filter)
}

export const InstitutionsApi = createApi({
  reducerPath: 'institutionsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
  }),
  endpoints: (build) => ({
    getInstitutionsInfo: build.query<InstitutionResponse, { instId }>({
      query: ({ instId }) => `institutions/${instId}`,
    }),
    getLocationsInfo: build.query<
      InstitutionResponse,
      { instId: string; locId: string }
    >({
      query: ({ instId, locId }) => `institutions/${instId}/locations/${locId}`,
    }),
    getLanguages: build.query<
      InstitutionResponse,
      { instId: string; locId: string }
    >({
      query: ({ instId, locId }) =>
        `institutions/${instId}/locations/${locId}/languages`,
    }),
    getStops: build.query<
      InstitutionResponse,
      { instId: string; locId: string; liveOnly?: boolean }
    >({
      query: ({ instId, locId, liveOnly = true }) => ({
        url: `institutions/${instId}/locations/${locId}/stops/tours`,
        params: { ...(liveOnly && { tour_status: 'live' }), size: 999 },
      }),
    }),
    getTourDescriptionById: build.query<
      InstitutionResponse,
      { instId: string; locId: string; descriptionId: number; language: string }
    >({
      query: ({ instId, locId, descriptionId, language }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/descriptions/${descriptionId}/${language}`,
        }
      },
    }),
    getPersonalizedStops: build.query<
      InstitutionResponse,
      { instId: string; locId: string; tourId: string; userkey: string }
    >({
      query: ({ instId, locId, userkey, tourId }) =>
        `institutions/${instId}/locations/${locId}/personalized-stops/me?tour_id=${tourId}&userkey=${userkey}`,
    }),
    getLocationModules: build.query<
      InstitutionResponse,
      { instId: string; locId: string }
    >({
      query: ({ instId, locId }) =>
        `institutions/${instId}/locations/${locId}/modules`,
    }),
    getPhotos: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        const filter = tourStopFilter(stopId, 'photos')
        return {
          url: `institutions/${instId}/locations/${locId}/photos`,
          params: {
            size: 999,
            filter,
            sort: 'update_time::DESC',
          },
        }
      },
    }),
    getPhotoById: build.query<
      InstitutionResponse,
      { instId: string; locId: string; photoId: number }
    >({
      query: ({ instId, locId, photoId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/photos/${photoId}`,
        }
      },
    }),
    getPanoramas: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        const filter = tourStopFilter(stopId, 'panoramas')
        return {
          url: `institutions/${instId}/locations/${locId}/panoramas`,
          params: {
            size: 999,
            filter,
            sort: 'update_time::DESC',
          },
        }
      },
    }),
    getPanoramaById: build.query<
      InstitutionResponse,
      { instId: string; locId: string; panoramaId: number }
    >({
      query: ({ instId, locId, panoramaId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/panoramas/${panoramaId}`,
        }
      },
    }),
    getVideos: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        const filter = tourStopFilter(stopId, 'videos')
        return {
          url: `institutions/${instId}/locations/${locId}/videos`,
          params: {
            size: 999,
            filter,
            sort: 'update_time::DESC',
          },
        }
      },
    }),
    getVideoById: build.query<
      InstitutionResponse,
      { instId: string; locId: string; videoId: number }
    >({
      query: ({ instId, locId, videoId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/videos/${videoId}`,
        }
      },
    }),
    getHotspots: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/hotspots`,
          params: {
            size: 999,
            sort: 'update_time::DESC',
          },
        }
      },
    }),
    getHotspotsByStopId: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/hotspots/stops/${stopId}`,
        }
      },
    }),
    getPositionalMedia: build.query<
      InstitutionResponse,
      { instId: string; locId: string }
    >({
      query: ({ instId, locId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/positional_media`,
        }
      },
    }),
    getPositionalMediaByStopId: build.query<
      InstitutionResponse,
      { instId: string; locId: string; stopId: number }
    >({
      query: ({ instId, locId, stopId }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/positional_media/stops/${stopId}`,
        }
      },
    }),
    getPositionalMediaVideos: build.query<
      InstitutionResponse,
      { instId: string; locId: string; videoIds: number[] }
    >({
      query: ({ instId, locId, videoIds }) => {
        return {
          url: `institutions/${instId}/locations/${locId}/videos`,
          params: {
            filter: JSON.stringify({
              properties: [
                {
                  column: 'videos.id',
                  op: 'in',
                  value: videoIds,
                },
              ],
              operation: 'AND',
            }),
          },
        }
      },
    }),
    getPoints: build.query<
      InstitutionResponse,
      { instId: string; locId: string; tourId: string }
    >({
      query: ({ instId, locId, tourId }) =>
        `institutions/${instId}/locations/${locId}/tours/${tourId}/points?size=500&sort=point::ASC`,
    }),
    getTrails: build.query<
      InstitutionResponse,
      { instId: string; locId: string; tourId: string }
    >({
      query: ({ instId, locId, tourId }) =>
        `institutions/${instId}/locations/${locId}/tours/${tourId}/trails?size=500`,
    }),
    getTrailsById: build.query<
      InstitutionResponse,
      { instId: string; locId: string; tourId: string; trailId: number }
    >({
      query: ({ instId, locId, tourId, trailId }) =>
        `institutions/${instId}/locations/${locId}/tours/${tourId}/trails/${trailId}`,
    }),
  }),
})

export const {
  useGetInstitutionsInfoQuery,
  useGetLocationsInfoQuery,
  useGetLanguagesQuery,
  useGetStopsQuery,
  useGetTourDescriptionByIdQuery,
  useGetPersonalizedStopsQuery,
  useLazyGetPersonalizedStopsQuery,
  useGetLocationModulesQuery,
  useGetPhotosQuery,
  useGetPhotoByIdQuery,
  useGetPanoramasQuery,
  useGetPanoramaByIdQuery,
  useGetVideosQuery,
  useGetVideoByIdQuery,
  useGetHotspotsQuery,
  useGetHotspotsByStopIdQuery,
  useGetPositionalMediaQuery,
  useGetPositionalMediaByStopIdQuery,
  useLazyGetPositionalMediaVideosQuery,
  useGetPointsQuery,
  useGetTrailsQuery,
  useGetTrailsByIdQuery,
} = InstitutionsApi
